'use client';

/**
 * WalletHero — V3 hero for /wallet.
 *
 * Frames the invisible problem before naming the product: the loyalty
 * card customers leave at the till is the one they forget. SUM ships
 * cards that live in their phone's wallet — Apple or Google — beside
 * their boarding passes and concert tickets.
 */

import Image from 'next/image';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import FeatureHero from '../_shared/sections/FeatureHero';

type Props = { lang: Locale };

const APPLE_WALLET_BADGE =
  'https://cdn.1cdn.app/application/UMD/26022610561174_US-UK_Add_to_Apple_Wallet_RGB_101421.svg';
const GOOGLE_WALLET_BADGE =
  'https://cdn.1cdn.app/application/UMD/26022610561140_enUS_add_to_google_wallet_add-wallet-badge.svg';

const WalletHero = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const visual = (
    <div className="flex flex-col items-center gap-5">
      <div
        className="rounded-xl flex items-center justify-center w-full"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          padding: 20,
        }}
      >
        <Image
          src={APPLE_WALLET_BADGE}
          alt={t('Wallet/HowItWorks/badge_apple_alt')}
          width={220}
          height={68}
          style={{ height: 68, width: 'auto' }}
        />
      </div>
      <div
        className="rounded-xl flex items-center justify-center w-full"
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          padding: 20,
        }}
      >
        <Image
          src={GOOGLE_WALLET_BADGE}
          alt={t('Wallet/HowItWorks/badge_google_alt')}
          width={220}
          height={68}
          style={{ height: 68, width: 'auto' }}
        />
      </div>
    </div>
  );

  return (
    <FeatureHero
      accent="#22D3EE"
      ariaLabel={t('Wallet/Hero/aria_label')}
      copy={{
        kicker: t('Wallet/Hero/badge'),
        headlineA: t('Wallet/Hero/title1'),
        headlineB: t('Wallet/Hero/title2'),
        subhead: t('Wallet/Hero/subtitle'),
        ctaPrimary: {
          label: t('Wallet/Hero/cta_primary'),
          href: `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL}/register`,
        },
        ctaSecondary: {
          label: t('Wallet/Hero/cta_secondary'),
          href: '#how-it-works',
        },
      }}
      visual={visual}
      visualVisibleOnMobile
    />
  );
};

export default WalletHero;
